/**
 * Tenant system settings management for the admin panel
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { SystemConfig, SystemConfigCreateRequest } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const emptyForm: SystemConfigCreateRequest = {
  config_key: '',
  config_value: {},
  config_type: 'system',
  description: '',
  is_active: true,
};

const SystemSettings: React.FC = () => {
  const {
    systemConfigs,
    systemConfigsLoading,
    systemConfigsError,
    loadSystemConfigs,
    createSystemConfig,
    updateSystemConfig,
    deleteSystemConfig,
    clearError,
  } = useAdmin();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SystemConfig | null>(null);
  const [form, setForm] = useState<SystemConfigCreateRequest>(emptyForm);
  const [valueJson, setValueJson] = useState('{}');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadSystemConfigs();
  }, [loadSystemConfigs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadSystemConfigs();
    } finally {
      setRefreshing(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setValueJson('{}');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (config: SystemConfig) => {
    setEditing(config);
    setForm({
      config_key: config.config_key,
      config_value: config.config_value,
      config_type: config.config_type,
      description: config.description,
      is_active: config.is_active,
    });
    setValueJson(JSON.stringify(config.config_value ?? {}, null, 2));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(valueJson || '{}');
    } catch {
      setFormError('Config value must be valid JSON');
      return;
    }
    if (!editing && !form.config_key.trim()) {
      setFormError('Config key is required');
      return;
    }
    try {
      if (editing) {
        await updateSystemConfig(editing.id, {
          config_value: parsed,
          description: form.description,
          is_active: form.is_active,
        });
      } else {
        await createSystemConfig({
          ...form,
          config_key: form.config_key.trim(),
          config_value: parsed,
        });
      }
      setDialogOpen(false);
    } catch {
      /* error surfaced via context */
    }
  };

  const handleDelete = async (config: SystemConfig) => {
    if (!window.confirm(`Delete setting "${config.config_key}"?`)) return;
    await deleteSystemConfig(config.id);
  };

  if (systemConfigsLoading && systemConfigs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <SettingsIcon />
          System Settings
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Setting
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {systemConfigsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {systemConfigsError}
        </Alert>
      )}

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {systemConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No system settings configured yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                systemConfigs.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {config.config_key}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{config.config_type}</TableCell>
                    <TableCell>{config.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.is_active ? 'Active' : 'Inactive'}
                        color={config.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {config.updated_at ? formatDate(config.updated_at) : formatDate(config.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(config)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Setting' : 'New Setting'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Config key"
                required
                disabled={!!editing}
                value={form.config_key}
                onChange={(e) => setForm((f) => ({ ...f, config_key: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.config_type}
                  label="Type"
                  disabled={!!editing}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      config_type: e.target.value as SystemConfigCreateRequest['config_type'],
                    }))
                  }
                >
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="feature_flag">Feature flag</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Config value (JSON)"
                value={valueJson}
                onChange={(e) => setValueJson(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active ?? true}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            {formError && (
              <Grid item xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings;
